import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, X, BookOpen } from 'lucide-react';
import { Button } from '../ui/button';
import { useElearningCartStats, useElearningCartActions } from '../../stores/useElearningCartStore';
import { useTenantContext } from '../../context/TenantContext';

export function MiniCartPreview() {
  const { tenant } = useTenantContext();
  const { items } = useElearningCartStats();
  const { removeItem } = useElearningCartActions();
  const [open, setOpen] = useState(false);

  // Fermer le mini-panier lorsque l'utilisateur clique en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (open && !target.closest('.mini-cart-container')) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  return (
    <div className="fixed top-32 md:top-24 right-4 md:right-10 z-50 mini-cart-container">
      <button
        aria-label="Aperçu du panier de formations"
        className="relative backdrop-blur-sm rounded-full p-3 shadow-lg hover:scale-110 transition-all duration-300"
        style={{
          backgroundColor: `${tenant?.config?.color2 || '#08062b'}e6`,
          borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = tenant?.config?.color1 || '#fe8d17';
          e.currentTarget.style.borderColor = tenant?.config?.color1 || '#fe8d17';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = `${tenant?.config?.color2 || '#08062b'}e6`;
          e.currentTarget.style.borderColor = `${tenant?.config?.color1 || '#fe8d17'}4d`;
        }}
        onClick={() => setOpen(v => !v)}
      >
        <ShoppingCart className="w-6 h-6 text-white transition-colors" />
        {items.length > 0 && (
          <span className="absolute -top-1 -right-1 rounded-full text-xs px-2 py-0.5 font-bold animate-pulse"
            style={{
              backgroundColor: tenant?.config?.color1 || '#fe8d17',
              color: tenant?.config?.color3 || '#ffffff'
            }}
          >
            {items.length}
          </span>
        )}
      </button>
      
      {open && (
        <div className="mt-2 w-80 backdrop-blur-sm rounded-xl shadow-2xl p-4 relative"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`,
            color: tenant?.config?.color3 || '#ffffff'
          }}
        >
          <button
            aria-label="Fermer le mini-panier"
            className="absolute top-2 right-2 text-xl font-bold transition-colors"
            style={{
              color: `${tenant?.config?.color4 || '#9ca3af'}b3`
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = tenant?.config?.color1 || '#fe8d17';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = `${tenant?.config?.color4 || '#9ca3af'}b3`;
            }}
            onClick={() => setOpen(false)}
            tabIndex={0}
          >
            <X size={16} />
          </button>
          
          <div className="flex justify-between items-center mb-2 pr-6">
            <span className="font-bold" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Mes formations</span>
            <Link to="/elearning/cart" className="hover:underline transition-colors"
              style={{
                color: tenant?.config?.color1 || '#fe8d17'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = `${tenant?.config?.color1 || '#fe8d17'}cc`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = tenant?.config?.color1 || '#fe8d17';
              }}
            >
              Voir tout
            </Link>
          </div>
          
          {items.length === 0 ? (
            <div className="text-sm py-2" style={{ color: `${tenant?.config?.color4 || '#9ca3af'}99` }}>Votre panier est vide.</div>
          ) : (
            <ul className="divide-y max-h-80 overflow-y-auto"
              style={{ borderColor: `${tenant?.config?.color1 || '#fe8d17'}33` }}
            >
              {items.map(item => (
                <li key={item.course_id} className="py-2 grid grid-cols-6 items-center gap-2">
                  {/* Image 1/6 */}
                  <div className="col-span-1 flex items-center">
                    {item.thumbnail_url ? (
                      <img
                        src={item.thumbnail_url}
                        alt={item.title}
                        className="w-10 h-10 rounded object-cover"
                        style={{
                          backgroundColor: `${tenant?.config?.color1 || '#fe8d17'}1a`,
                          borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`
                        }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x400';
                        }}
                        aria-label={`Image de ${item.title}`}
                      />
                    ) : (
                      <div className="w-10 h-10 flex items-center justify-center rounded"
                        style={{
                          backgroundColor: `${tenant?.config?.color1 || '#fe8d17'}1a`,
                          borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`
                        }}
                      >
                        <BookOpen className="w-5 h-5" style={{ color: `${tenant?.config?.color4 || '#9ca3af'}99` }} />
                      </div>
                    )}
                  </div>
                  
                  {/* Titre + quantité 3/6 */}
                  <div className="col-span-3 min-w-0">
                    <div className="font-semibold truncate text-sm" style={{ color: tenant?.config?.color3 || '#ffffff' }}>{item.title}</div>
                    <div className="text-xs" style={{ color: `${tenant?.config?.color4 || '#9ca3af'}b3` }}>Formation</div>
                  </div>
                  
                  {/* Prix 1/6 */}
                  <div className="col-span-1 text-sm font-bold text-right" style={{ color: tenant?.config?.color1 || '#fe8d17' }}>
                    {new Intl.NumberFormat('fr-FR').format(item.price)} {item.currency}
                  </div>
                  
                  {/* Icône suppression 1/6 */}
                  <div className="col-span-1 flex justify-end">
                    <button
                      aria-label="Retirer du panier"
                      className="p-1 transition-colors rounded hover:bg-opacity-20"
                      style={{
                        color: `${tenant?.config?.color4 || '#9ca3af'}66`
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = tenant?.config?.color1 || '#fe8d17';
                        e.currentTarget.style.backgroundColor = `${tenant?.config?.color1 || '#fe8d17'}1a`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = `${tenant?.config?.color4 || '#9ca3af'}66`;
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                      onClick={() => removeItem(item.course_id)}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          
          {items.length > 0 && (
            <div className="mt-3 pt-3 border-t"
              style={{ borderColor: `${tenant?.config?.color1 || '#fe8d17'}33` }}
            >
              <div className="flex justify-between items-center mb-3">
                <span className="font-medium" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Total:</span>
                <span className="font-bold" style={{ color: tenant?.config?.color1 || '#fe8d17' }}>
                  {new Intl.NumberFormat('fr-FR').format(
                    items.reduce((total, item) => total + item.price, 0)
                  )} {items[0]?.currency || 'FCFA'}
                </span>
              </div>
              <Link 
                to="/elearning/checkout" 
                className="block w-full text-center py-2 rounded-md transition-all duration-300 font-medium"
                style={{
                  backgroundColor: tenant?.config?.color1 || '#fe8d17',
                  color: tenant?.config?.color3 || '#ffffff',
                  backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e07a10'})`
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#e07a10'}, ${tenant?.config?.color1 || '#fe8d17'})`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e07a10'})`;
                }}
                onClick={() => setOpen(false)}
              >
                Passer à la caisse
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
